import { useState, useEffect, useRef } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import DialogLoading from '../../components/feedback/DialogLoading';
import AppButton from '../../components/ui/AppButton';
import { categoryOptions, productSizeOptions } from '../../constants/reward';
import { useGetRewardList, useUpdateReward } from '../../hooks/useReward';
import { rewardSchema } from '../../schemas/reward';
import { useDialogStore } from '../../stores/dialogStore';

const AdminRewardDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const openDialog = useDialogStore((state) => state.openDialog);
  const [loadingDialog, setLoadingDialog] = useState(false);

  const { data: rewardDetail, isLoading: isLoadingReward } = useGetRewardList({ id: params.id });
  const { mutateAsync: updateReward } = useUpdateReward();

  const inputRef = useRef(null);
  const [file, setFile] = useState(null);

  const formik = useFormik({
    initialValues: {
      image: '',
      sku: '',
      name: '',
      detail: '',
      point: '',
      value: '',
      quantity: '',
      unit: '',
      stock: '',
      limit: '',
      product_size: '',
      category: '',
      type_delivery: '',
      createTimestamp: '',
      updateTimestamp: '',
    },
    enableReinitialize: true,
    validationSchema: rewardSchema,
    onSubmit: async (data) => {
      try {
        setLoadingDialog(true);

        const formData = new FormData();
        if (data.sku) formData.append('sku', data.sku);
        if (data.name) formData.append('name', data.name);
        if (data.detail) formData.append('detail', data.detail);
        if (data.point) formData.append('point', data.point);
        if (data.value) formData.append('value', data.value);
        if (data.quantity) formData.append('quantity', data.quantity);
        if (data.unit) formData.append('unit', data.unit);
        if (data.stock) formData.append('stock', data.stock);
        if (data.product_size) formData.append('product_size', data.product_size);
        if (data.category) formData.append('category', data.category);
        if (data.type_delivery) formData.append('type_delivery', data.type_delivery);
        if (data.limit) formData.append('limit', data.limit);

        if (file) {
          formData.append('image', file);
        }

        const response = await updateReward({
          params: { id: params.id },
          dto: formData,
        });

        if (response.Code === '20100') {
          openDialog({
            title: 'สำเร็จ',
            message: 'แก้ไขข้อมูลของรางวัลสำเร็จ',
            variant: 'success',
            onCloseCallback: () => {
              navigate('/admin#4');
            },
          });
        }
      } catch (e) {
        console.error(e);
        openDialog({
          title: 'เกิดข้อผิดพลาด',
          message: 'แก้ไขข้อมูลของรางวัลไม่สำเร็จ',
          variant: 'error',
        });
      } finally {
        setLoadingDialog(false);
      }
    },
  });

  const handleChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (
      !isLoadingReward &&
      rewardDetail &&
      rewardDetail.Code === '20000' &&
      rewardDetail.Data.data.length > 0
    ) {
      const data = rewardDetail.Data.data[0];

      formik.setValues({
        image: data.image[0] || '',
        sku: data.sku || '',
        name: data.name || '',
        detail: data.detail || '',
        point: data.point || '',
        value: data.value || '',
        quantity: data.quantity || '',
        unit: data.unit || '',
        stock: data.stock || '',
        limit: data.limit || '',
        product_size: data.product_size || '',
        category: data.category || '',
        type_delivery: data.type_delivery || '',
        createTimestamp: data.create_timestamp
          ? dayjs(data.create_timestamp).format('DD/MM/YYYY HH:mm:ss')
          : '',
        updateTimestamp: data.update_timestamp
          ? dayjs(data.update_timestamp).format('DD/MM/YYYY HH:mm:ss')
          : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardDetail, isLoadingReward]);

  return (
    <>
      <Helmet>
        <title>จัดการของรางวัล | Caltex StarCard</title>
      </Helmet>

      <Box>
        <Box
          component="section"
          maxWidth="lg"
          mx="auto"
          sx={{ padding: '40px 1.25rem', height: '100%', overflow: 'auto' }}
        >
          <Box sx={{ position: 'relative' }}>
            <Typography
              sx={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: '#002B38',
                textAlign: 'center',
                marginBottom: '1.25rem',
              }}
            >
              จัดการของรางวัล
            </Typography>

            <IconButton
              onClick={() => navigate('/admin#4')}
              sx={{
                width: '120px',
                height: '45px',
                fontSize: '1.5rem',
                color: '#004B61',
                position: 'absolute',
                top: '0',
                left: '0',
                padding: '0',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                zIndex: '10',

                '&:hover': {
                  opacity: '.85',
                  backgroundColor: '#fafafa',
                },
              }}
            >
              <ArrowBackIosIcon
                sx={{
                  fontSize: '1rem',
                  color: '#004B61',
                }}
              />
              ย้อนกลับ
            </IconButton>
          </Box>

          <Box my={3} sx={{ backgroundColor: 'white', borderRadius: '8px' }}>
            {isLoadingReward && (
              <Box sx={{ padding: '40px' }}>
                <CircularProgress sx={{ display: 'block', margin: '50px auto' }} />
              </Box>
            )}

            {!isLoadingReward && (
              <Box
                component="form"
                onSubmit={formik.handleSubmit}
                sx={{
                  display: 'grid',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '20px',
                  gridTemplateColumns: '0.7fr 1fr',
                }}
              >
                {/* Reward image */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {formik.values.image ? (
                    <Box
                      component="img"
                      src={formik.values.image}
                      alt={formik.values.name}
                      sx={{ width: '85%' }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: '80px', color: '#333', textAlign: 'center' }} />
                  )}
                </Box>

                {/* Reward Detail */}
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '40px',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '15px',
                  }}
                >
                  {/* SKU */}
                  <Box>
                    <Label>SKU:</Label>
                    <Input disabled value={formik.values.sku} />
                  </Box>
                  {/* Name */}
                  <Box>
                    <Label htmlFor="name">ชื่อ:</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.name && formik.touched.name && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.name}</Box>
                    )}
                  </Box>
                  {/* Description */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label htmlFor="detail">รายละเอียด:</Label>
                    <Input
                      id="detail"
                      name="detail"
                      multiline
                      minRows={3}
                      value={formik.values.detail}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.detail && formik.touched.detail && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.detail}</Box>
                    )}
                  </Box>
                  {/* Point */}
                  <Box>
                    <Label htmlFor="point">คะแนนที่ใช้ในการแลก:</Label>
                    <Input
                      id="point"
                      name="point"
                      value={formik.values.point}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.point && formik.touched.point && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.point}</Box>
                    )}
                  </Box>
                  {/* Value */}
                  <Box>
                    <Label htmlFor="value">มูลค่า (บาท):</Label>
                    <Input
                      id="value"
                      name="value"
                      value={formik.values.value}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.value && formik.touched.value && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.value}</Box>
                    )}
                  </Box>
                  {/* Quantity */}
                  <Box>
                    <Label htmlFor="quantity">จำนวนรางวัลที่ได้รับ:</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      value={formik.values.quantity}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.quantity && formik.touched.quantity && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.quantity}</Box>
                    )}
                  </Box>
                  {/* Unit */}
                  <Box>
                    <Label htmlFor="unit">หน่วย:</Label>
                    <Input
                      id="unit"
                      name="unit"
                      value={formik.values.unit}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.unit && formik.touched.unit && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.unit}</Box>
                    )}
                  </Box>
                  {/* Remaining */}
                  <Box>
                    <Label htmlFor="stock">คงเหลือ:</Label>
                    <Input
                      id="stock"
                      name="stock"
                      value={formik.values.stock}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.stock && formik.touched.stock && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.stock}</Box>
                    )}
                  </Box>
                  {/* Limit */}
                  <Box>
                    <Label htmlFor="stock">จำกัดการแลกต่อบริษัท:</Label>
                    <Input
                      id="limit"
                      name="limit"
                      value={formik.values.limit}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.limit && formik.touched.limit && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.limit}</Box>
                    )}
                  </Box>
                  {/* Size */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label htmlFor="product_size">ขนาด:</Label>
                    <Select
                      id="product_size"
                      name="product_size"
                      value={formik.values.product_size}
                      onChange={formik.handleChange}
                      sx={{
                        display: 'block',
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        height: '42px',
                        color: '#004B61',

                        '& .MuiSelect-select': {
                          fontSize: '1.25rem',
                          padding: '10px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#004B61',
                        },
                      }}
                    >
                      {productSizeOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value} sx={{ fontSize: '1.25rem' }}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Box sx={{ fontSize: '1.25rem', color: '#505050' }}>
                      *
                      {
                        productSizeOptions.find((item) => item.value === formik.values.product_size)
                          ?.desc
                      }
                    </Box>
                    {formik.errors.product_size && formik.touched.product_size && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>
                        {formik.errors.product_size}
                      </Box>
                    )}
                  </Box>
                  {/* Category */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label htmlFor="product_size">ประเภท:</Label>
                    <Select
                      id="category"
                      name="category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      sx={{
                        display: 'block',
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        height: '42px',
                        color: '#004B61',

                        '& .MuiSelect-select': {
                          fontSize: '1.25rem',
                          padding: '10px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#004B61',
                        },
                      }}
                    >
                      {categoryOptions.map((item) => (
                        <MenuItem key={item.value} value={item.value} sx={{ fontSize: '1.25rem' }}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.errors.category && formik.touched.category && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>{formik.errors.category}</Box>
                    )}
                  </Box>
                  {/* Type delivery */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label htmlFor="product_size">บริการจัดส่ง:</Label>
                    <Select
                      id="type_delivery"
                      name="type_delivery"
                      value={formik.values.type_delivery}
                      onChange={formik.handleChange}
                      displayEmpty
                      sx={{
                        display: 'block',
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        height: '42px',
                        color: '#004B61',

                        '& .MuiSelect-select': {
                          fontSize: '1.25rem',
                          padding: '10px',
                        },

                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#004B61',
                        },
                      }}
                    >
                      <MenuItem value={''} sx={{ fontSize: '1.25rem' }} disabled>
                        เลือกบริการจัดส่ง
                      </MenuItem>
                      <MenuItem value={'packhai'} sx={{ fontSize: '1.25rem' }}>
                        PackHai
                      </MenuItem>
                      <MenuItem value={'self delivery'} sx={{ fontSize: '1.25rem' }}>
                        Self Delivery
                      </MenuItem>
                      <MenuItem value={'line chat'} sx={{ fontSize: '1.25rem' }}>
                        Line Chat
                      </MenuItem>
                    </Select>
                    {formik.errors.type_delivery && formik.touched.type_delivery && (
                      <Box sx={{ color: 'red', fontSize: '1.25rem' }}>
                        {formik.errors.type_delivery}
                      </Box>
                    )}
                  </Box>
                  {/* Image */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label>รูปภาพ:</Label>
                    <AppButton
                      variant="text"
                      component="label"
                      sx={{
                        display: 'block',
                        color: '#004B61',
                        backgroundColor: 'white',
                        boxShadow: 'none',
                        border: '1px solid #004B61',
                        borderRadius: '8px',

                        '&:hover': {
                          backgroundColor: 'white',
                          opacity: 0.85,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '1rem',
                          }}
                        >
                          {file ? file.name : 'Upload Image'}
                        </Box>

                        {file && (
                          <IconButton size="small" onClick={handleDelete}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </Box>

                      <input
                        ref={inputRef}
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleChange}
                      />
                    </AppButton>
                  </Box>
                  {/* Create timestamp */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label>Create timestamp:</Label>
                    <Input disabled value={formik.values.createTimestamp} />
                  </Box>
                  {/* Update timestamp */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Label>Update timestamp:</Label>
                    <Input disabled value={formik.values.updateTimestamp} />
                  </Box>
                  {/* Submit */}
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <AppButton
                      variant="text"
                      type="submit"
                      sx={{
                        width: '150px',
                        height: '45px',
                        borderRadius: '8px',
                        backgroundColor: '#E1000C',
                        color: 'white',
                        marginTop: '20px',
                        fontSize: '1.25rem',

                        '&:hover': {
                          backgroundColor: '#E1000C',
                          color: 'white',
                          opacity: '.85',
                        },
                      }}
                    >
                      บันทึก
                    </AppButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <DialogLoading open={loadingDialog} message="กำลังแก้ไขข้อมูลของรางวัล" />
        </Box>
      </Box>
    </>
  );
};

const Label = (props) => {
  return (
    <Box
      component="label"
      sx={{
        fontSize: '1.25rem',
        fontWeight: '500',
        color: '#333',
        marginBottom: '4px',
      }}
      {...props}
    >
      {props.children}
    </Box>
  );
};

const Input = (props) => {
  return (
    <TextField
      fullWidth
      sx={{
        borderRadius: '8px',

        '& .MuiInputBase-root': {
          fontSize: '1.25rem',
          borderRadius: '8px',
          backgroundColor: 'white',
        },
        '& .MuiInputBase-multiline': {
          padding: '5px 0',
        },
        '& .MuiInputBase-input': {
          padding: '5px 10px',
          height: '32px',
          color: '#004B61',
        },
        '& .MuiInputBase-input::placeholder': {},
        '& .MuiOutlinedInput-notchedOutline': {
          fontSize: '1.25rem',
          borderRadius: '8px',
          border: '1px solid #004B61',
        },
      }}
      {...props}
    />
  );
};

export default AdminRewardDetailPage;
